"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.defaultDimensionsGenerator = exports.defWithDimensions = exports.defWithDateFormat = exports.defWithPostProcessing = exports.defWithExecConfig = exports.defWithSorting = exports.newDefForInsight = exports.newDefForBuckets = exports.newDefForItems = exports.emptyDef = void 0;
var tslib_1 = require("tslib");
// (C) 2019-2021 GoodData Corporation
var attribute_1 = require("../attribute");
var dimension_1 = require("../base/dimension");
var buckets_1 = require("../buckets");
var bucketArray_1 = require("../buckets/bucketArray");
var insight_1 = require("../../insight");
var measure_1 = require("../measure");
var index_1 = require("./index");
var isEmpty_1 = tslib_1.__importDefault(require("lodash/isEmpty"));
var ts_invariant_1 = tslib_1.__importDefault(require("ts-invariant"));
var validation_1 = require("./validation");
/**
 * Creates new, empty execution definition for the provided workspace.
 *
 * @param workspace - workspace to calculate on
 * @returns always new instance
 * @public
 */
function emptyDef(workspace) {
    return {
        workspace: workspace,
        buckets: [],
        attributes: [],
        measures: [],
        dimensions: [],
        filters: [],
        sortBy: [],
        postProcessing: {},
    };
}
exports.emptyDef = emptyDef;
/**
 * Prepares a new execution definition for a list of attributes and measures, optionally filtered using the
 * provided filters.
 *
 * This function MUST be used to implement IExecutionFactory.forItems();
 *
 * @param workspace - workspace to execute against, must not be empty
 * @param items - list of attributes and measures, must not be empty
 * @param filters - list of filters, may not be provided
 * @public
 */
function newDefForItems(workspace, items, filters) {
    if (filters === void 0) { filters = []; }
    ts_invariant_1.default(workspace, "workspace to create exec def for must be specified");
    ts_invariant_1.default(items, "items to create exec def from must be specified");
    var def = tslib_1.__assign(tslib_1.__assign({}, emptyDef(workspace)), { attributes: items.filter(attribute_1.isAttribute), measures: items.filter(measure_1.isMeasure) });
    validation_1.defValidate(def);
    return index_1.defWithFilters(def, filters);
}
exports.newDefForItems = newDefForItems;
/**
 * Prepares a new execution definition for a list of buckets. Attributes and measures WILL be transferred to the
 * execution in natural order:
 *
 * - Order of items within a bucket is retained in the execution
 * - Items from first bucket appear before items from second bucket
 *
 * Or more specifically, given two buckets with items as [A1, A2, M1] and [A3, M2, M3], the resulting
 * prepared execution WILL have definition with attributes = [A1, A2, A3] and measures = [M1, M2, M3]
 *
 * This function MUST be used to implement IExecutionFactory.forBuckets();
 *
 * @param workspace - workspace to execute against, must not be empty
 * @param buckets - list of buckets with attributes and measures, must be non empty, must have at least one attr or measure
 * @param filters - optional, may not be provided
 * @public
 */
function newDefForBuckets(workspace, buckets, filters) {
    if (filters === void 0) { filters = []; }
    ts_invariant_1.default(workspace, "workspace to create exec def for must be specified");
    ts_invariant_1.default(buckets, "buckets to create exec def from must be specified");
    var def = tslib_1.__assign(tslib_1.__assign({}, emptyDef(workspace)), { buckets: buckets, attributes: bucketArray_1.bucketsAttributes(buckets), measures: bucketArray_1.bucketsMeasures(buckets) });
    validation_1.defValidate(def);
    return index_1.defWithFilters(def, filters);
}
exports.newDefForBuckets = newDefForBuckets;
/**
 * Prepares a new execution definition for the provided insight. Buckets with attributes and measures WILL be used
 * to obtain attributes and measures - the behavior WILL be same as in forBuckets() function. Filters, sort by
 * and totals in the insight WILL be included in the prepared execution.
 *
 * Additionally, an optional list of additional filters WILL be merged with the filters already defined in
 * the insight.
 *
 * - Attributes and measures from insight's buckets are distributed into definition attributes and measures
 *   in natural order.
 * - Insight filters are added into definition
 * - Insight sorts are added into definition
 * - Insight totals are added into definition
 *
 * This function MUST be used to implement IExecutionFactory.forInsight();
 *
 * @param workspace - workspace to execute against, must not be empty
 * @param insight - insight to create execution for, must have buckets which must have some attributes or measures in them
 * @param filters - optional, may not be provided
 * @public
 */
function newDefForInsight(workspace, insight, filters) {
    if (filters === void 0) { filters = []; }
    ts_invariant_1.default(workspace, "workspace to create exec def for must be specified");
    ts_invariant_1.default(insight, "insight to create exec def from must be specified");
    var def = newDefForBuckets(workspace, insight_1.insightBuckets(insight));
    validation_1.defValidate(def);
    var extraFilters = filters ? filters : [];
    var filteredDef = index_1.defWithFilters(def, tslib_1.__spreadArrays(insight_1.insightFilters(insight), extraFilters));
    return index_1.defSetSorts(filteredDef, insight_1.insightSorts(insight));
}
exports.newDefForInsight = newDefForInsight;
/**
 * Changes sorting in the definition. Any sorting settings accumulated so far WILL be wiped out.
 *
 * This function MUST be used to implement IPreparedExecution.withSorting();
 *
 * @param definition - definition to alter with sorting
 * @param sorts - items to sort by
 * @returns new execution with the updated sorts
 * @public
 */
function defWithSorting(definition, sorts) {
    return index_1.defSetSorts(definition, sorts);
}
exports.defWithSorting = defWithSorting;
/**
 * Changes additional execution configuration in the definition.
 * Any additional execution configuration settings accumulated so far WILL be wiped out.
 *
 * This function MUST be used to implement IPreparedExecution.withExecConfig();
 *
 * @param definition - definition to alter with execution config
 * @param config - configuration
 * @returns new execution with the updated sorts
 * @public
 */
function defWithExecConfig(definition, config) {
    return index_1.defSetExecConfig(definition, config);
}
exports.defWithExecConfig = defWithExecConfig;
/**
 * Changes the postProcessing of a definition.
 *
 * @param definition - execution definition to alter with postProcessing
 * @param postProcessing - configuration that should be done with the data after they are obtained from the server
 *  and before they are passed to the user
 * @returns new execution with the specified postProcessing
 * @public
 */
function defWithPostProcessing(definition, postProcessing) {
    return index_1.defSetPostProcessing(definition, postProcessing);
}
exports.defWithPostProcessing = defWithPostProcessing;
/**
 * Changes the dateFormat of a postProcessing, other properties of postProcessing (if any) remain unchanged.
 * This function will call defWithPostProcessing to update definition with the new postProcessing.
 *
 * This function MUST be used to implement IPreparedExecution.withDateFormat();
 *
 * @param definition - execution definition to alter with postProcessing
 * @param dateFormat - Format to be applied to the dates in an AFM execution response.
 * @returns new execution with postProcessing updated with the specified dateFormat
 * @public
 */
function defWithDateFormat(definition, dateFormat) {
    var currentPostProcessing = definition.postProcessing || {};
    var postProcessing = tslib_1.__assign(tslib_1.__assign({}, currentPostProcessing), { dateFormat: dateFormat });
    return defWithPostProcessing(definition, postProcessing);
}
exports.defWithDateFormat = defWithDateFormat;
/**
 * Configures dimensions in the exec definition. Any dimension settings accumulated so far WILL be wiped out.
 * If dims is array if dimensions, they will be used as is. If it is an array whose first element is dimension
 * generation function, then the function will be called to obtain dimensions.
 *
 * This function MUST be used to implement IPreparedExecution.withDimensions(); its parameters are constructed in
 * a way that it can handle both signatures of the withDimensions().
 *
 * @param definition - execution definition to alter
 * @param dims - dimensions to set
 * @returns new execution with the updated dimensions
 * @public
 */
function defWithDimensions(definition) {
    var dims = [];
    for (var _i = 1; _i < arguments.length; _i++) {
        dims[_i - 1] = arguments[_i];
    }
    return index_1.defSetDimensions(definition, toDimensions(dims, definition));
}
exports.defWithDimensions = defWithDimensions;
function toDimensions(dimsOrGen, def) {
    if (!dimsOrGen || isEmpty_1.default(dimsOrGen)) {
        return [];
    }
    var maybeGenerator = dimsOrGen[0];
    if (typeof maybeGenerator === "function") {
        return maybeGenerator(def);
    }
    return dimsOrGen.filter(dimension_1.isDimension);
}
function defaultDimensionsWithBuckets(buckets) {
    var firstBucket = buckets[0], otherBuckets = buckets.slice(1);
    if (bucketArray_1.bucketsIsEmpty(otherBuckets)) {
        if (buckets_1.bucketMeasures(firstBucket).length) {
            return dimension_1.newTwoDimensional([dimension_1.MeasureGroupIdentifier], buckets_1.bucketAttributes(firstBucket).map(attribute_1.attributeLocalId));
        }
        return [dimension_1.newDimension(buckets_1.bucketAttributes(firstBucket).map(attribute_1.attributeLocalId))];
    }
    var firstDim = buckets_1.bucketAttributes(firstBucket).map(attribute_1.attributeLocalId);
    var secondDim = bucketArray_1.bucketsAttributes(otherBuckets).map(attribute_1.attributeLocalId);
    if (buckets_1.bucketMeasures(firstBucket).length) {
        firstDim.push(dimension_1.MeasureGroupIdentifier);
    }
    else {
        secondDim.push(dimension_1.MeasureGroupIdentifier);
    }
    return dimension_1.newTwoDimensional(firstDim, secondDim);
}
function defaultDimensionsWithoutBuckets(definition) {
    if (definition.measures.length) {
        return dimension_1.newTwoDimensional([dimension_1.MeasureGroupIdentifier], definition.attributes.map(attribute_1.attributeLocalId));
    }
    return [dimension_1.newDimension(definition.attributes.map(attribute_1.attributeLocalId))];
}
/**
 * Default dimension generator for execution definition behaves as follows:
 *
 * - If the definition was created WITHOUT 'buckets', then:
 *   - If there are no measures specified, then single dimension will be returned and will contain all attributes
 *   - If there are measures, then two dimensions will be returned; measureGroup will be in the first dimension
 *     and all attributes in the second dimension
 *
 * If the definition was created WITH 'buckets' then:
 *   - If there is just one bucket and it contains only attributes, then single dimension with all attributes will be returned
 *   - If there is just one bucket and it contains both attributes and measures, then two dimensions will be returned:
 *     measureGroup will be in first dimension, all other attributes in the second dimension
 *   - If there are multiple buckets, then all attributes from first bucket will be in first dimension and all attributes
 *     from other buckets in the second dimension. If the first bucket contains measure(s), then the MeasureGroup will
 *     be in first dimension. Otherwise it will be in second dimension.
 *
 * @param definition - execution definition to get dims for
 * @public
 */
function defaultDimensionsGenerator(definition) {
    ts_invariant_1.default(definition, "definition must be specified");
    var buckets = definition.buckets;
    return !bucketArray_1.bucketsIsEmpty(buckets)
        ? defaultDimensionsWithBuckets(buckets)
        : defaultDimensionsWithoutBuckets(definition);
}
exports.defaultDimensionsGenerator = defaultDimensionsGenerator;
//# sourceMappingURL=factory.js.map